import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Logo } from "../../components/shared";

function inviteToken() {
  return new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token") || "";
}

export default function ResidentActivation() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const token = inviteToken();

  useEffect(() => {
    if (!token) {
      setStatus({ invalid: true });
      return;
    }
    api.post("/resident/activation/status", { token }).then((response) => {
      setStatus(response.data);
    }).catch(() => setStatus({ invalid: true }));
  }, [token]);

  const register = async () => {
    setBusy(true);
    try {
      await api.post("/resident/activation/register", { ...form, token });
      await checkAuth();
      window.history.replaceState({}, document.title, "/resident");
      navigate("/resident");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Activation could not be completed");
    } finally {
      setBusy(false);
    }
  };

  const claim = async () => {
    setBusy(true);
    try {
      await api.post("/resident/activation/claim", { token });
      await checkAuth();
      window.history.replaceState({}, document.title, "/resident");
      navigate("/resident");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Activation could not be completed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8" data-testid="resident-activation-page">
      <div className="mx-auto max-w-md">
        <Logo />
        <section className="mt-10 border border-slate-200 bg-white p-6 sm:p-8">
          <ShieldCheck className="h-7 w-7 text-primary" />
          <h1 className="mt-5 font-display text-3xl font-bold text-slate-900">Resident access</h1>
          {!status ? (
            <div className="py-10 text-center" data-testid="resident-activation-loading">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
            </div>
          ) : status.invalid ? (
            <p className="mt-3 text-sm leading-6 text-slate-600" data-testid="resident-activation-invalid">
              This invitation is unavailable. Please contact your Society Admin for a new invitation.
            </p>
          ) : status.existing_account ? (
            <div className="mt-4 space-y-4" data-testid="resident-activation-existing-account">
              <p className="text-sm leading-6 text-slate-600">
                This email already has a MySocietyMitra account.
              </p>
              {user ? (
                <Button data-testid="resident-activation-claim" disabled={busy} onClick={claim}>
                  {busy ? <Loader2 className="animate-spin" /> : null}
                  Link resident access
                </Button>
              ) : (
                <Button data-testid="resident-activation-login" onClick={() => navigate("/login")}>
                  Sign in, then open this invitation again
                </Button>
              )}
            </div>
          ) : (
            <div className="mt-5 space-y-4" data-testid="resident-activation-register-form">
              <p className="text-sm leading-6 text-slate-600">
                Create a password for your resident account. Your email stays private and is already verified.
              </p>
              <div>
                <Label htmlFor="resident-activation-name">Name</Label>
                <Input
                  data-testid="resident-activation-name"
                  id="resident-activation-name"
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  value={form.name}
                />
              </div>
              <div>
                <Label htmlFor="resident-activation-password">Password</Label>
                <Input
                  data-testid="resident-activation-password"
                  id="resident-activation-password"
                  minLength="8"
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                  type="password"
                  value={form.password}
                />
              </div>
              <Button data-testid="resident-activation-submit" disabled={busy} onClick={register}>
                {busy ? <Loader2 className="animate-spin" /> : null}
                Activate access
              </Button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}